import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Computer as ComputerIcon, Business as BusinessIcon, Schedule as ScheduleIcon, People as PeopleIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshPaperStyles, getRefreshTableStyles } from '../utils/refreshAnimation';
import { serverService } from '../services/serverService';
import type { Server } from '../types/server';
import { formatDateToTR } from '../utils/dateUtils';

const Servers = () => {
  const { refreshTrigger, autoRefresh, isRefreshing } = useFilter();
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);
  const navigate = useNavigate();

  const loadServers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await serverService.getAll();
      setServers(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Sunucular yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServers();
  }, [loadServers, refreshTrigger]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadServers();
    }, 60000); // 60 saniye

    return () => clearInterval(interval);
  }, [autoRefresh, loadServers]);

  const handleCreate = () => {
    navigate('/servers/new');
  };

  const handleEdit = (id: string) => {
    navigate(`/servers/${id}/edit`);
  };

  const handleDeleteClick = (server: Server) => {
    setSelectedServer(server);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedServer) return;

    try {
      await serverService.delete(selectedServer.id);
      await loadServers();
      setDeleteDialogOpen(false);
      setSelectedServer(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Sunucu silinirken bir hata oluştu');
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Sunucular
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Yeni Sunucu
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {servers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', ...getRefreshPaperStyles(isRefreshing) }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Henüz sunucu bulunmuyor.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ mt: 2 }}
          >
            İlk Sunucuyu Oluştur
          </Button>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            ...getRefreshPaperStyles(isRefreshing),
          }}
        >
          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small" sx={getRefreshTableStyles(isRefreshing)}>
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ComputerIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Sunucu Adı
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Şirket Grubu
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Açıklama</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <PeopleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Agent Sayısı
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Oluşturulma Tarihi
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }} align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servers.map((server, index) => (
                  <TableRow
                    key={server.id}
                    hover
                    sx={{
                      bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                      '&:hover': { bgcolor: 'action.selected' },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <ComputerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {server.name}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Chip label={server.tenantName} size="small" sx={{ height: 22, fontSize: '0.75rem' }} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Chip
                        label={server.isActive ? 'Aktif' : 'Pasif'}
                        size="small"
                        color={server.isActive ? 'success' : 'default'}
                        sx={{ height: 22, fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{server.description || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{server.agentCount}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {formatDateToTR(server.createdAt)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }} align="right">
                      <IconButton size="small" onClick={() => handleEdit(server.id)} color="primary" aria-label="Düzenle">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(server)} color="error" aria-label="Sil">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Sunucu Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedServer?.name} adlı sunucuyu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Layout>
  );
};

export default Servers;










